import {
  applyEarnResults, BOOSTER, boosterDupeRefund, EARN_BATCH_MAX, spendPrice,
  validateEarnEvent,
  type EarnEvent, type SpendSku,
} from '../../shared/economy'
import { PROGRESS_EPOCH } from '../../shared/progressEpoch'
import { nativeHadUid, rememberNativeUid, releaseFailedNativeUidReservation, clearNativePersistence } from '~/utils/nativePersistence'
import { apiFetch } from '~/utils/api'
import { PROGRESS_KEYS } from '~/utils/progressEpoch'
import { getCreatureDef, rollBoosterPack } from '~/data/creatures'

/**
 * 🌟 Stardust — the GLOBAL currency, earned across runs, spent in the
 * title-screen Shop.
 *
 * 🏦 v4.92 (per Javi: "stardust being cheatable defeats the whole purpose
 * of the game"): the balance is SERVER-AUTHORITATIVE. This composable is
 * now a ledger CLIENT:
 *
 *   · Earnings are EVENTS ("act 3 cleared in run X"), queued in a local
 *     outbox and flushed to /api/wallet/earn — the server computes every
 *     amount from shared/economy.ts and admits each key once. Offline
 *     play queues; nothing is lost, it just reads as pending.
 *   · The displayed balance = last server-confirmed balance + the
 *     outbox's estimated pending, so an act clear still lands instantly.
 *   · Spends FAIL CLOSED through /api/wallet/spend — no server
 *     confirmation, no sale.
 *   · Identity: any signed-in uid works; with nobody signed in the first
 *     flush/spend lazily creates an ANONYMOUS Firebase user (requires the
 *     Anonymous provider enabled in the Firebase console — until then the
 *     outbox simply holds). Google sign-in later inherits the anonymous
 *     balance via the transfer-code dance in useAuth.
 *   · One-time migration: the pre-ledger earned/spent counters import
 *     once per ACCOUNT (server-capped amnesty), then zero locally.
 *
 * 🧪 LOCAL MODE: the localStorage flag below reverts to the pre-v4.92
 * device-local counters — no network, no auth. verify.mjs seeds it so UI
 * checks stay deterministic; a player setting it only forks their own
 * display, because the ledger never reads client counters.
 */
const STORAGE_KEY = 'elemental-dispatch-stardust'
const EARNED_KEY = 'elemental-dispatch-stardust-earned'
const SPENT_KEY = 'elemental-dispatch-stardust-spent'
const LOCAL_MODE_KEY = 'elemental-dispatch-wallet-local'
const CACHE_KEY = 'elemental-dispatch-wallet'
const OUTBOX_KEY = 'elemental-dispatch-wallet-outbox'

export {
  stardustForAct, stardustAscensionMult, firstClearStardust,
  puzzleClearStardust, STARDUST_PER_ACT, GAUNTLET_STARDUST_RAMP,
} from '../../shared/economy'

interface OutboxItem { uid?: string; sourceUid?: string; key: string; body: EarnEvent; estimate: number; at: number }
interface EarnResult {
  key: string
  /** 🗝️ v6.36: position in the request. Absent from older servers,
   * which is why applyEarnResults also reads `invalid:<n>` and the key. */
  index?: number
  status: 'credited' | 'duplicate' | 'rejected' | 'deferred'
  amount: number
}
export type SpendOutcome = { ok: true } | { ok: false; reason: 'signin' | 'offline' | 'funds' | 'syncing' }

function readNum(key: string): number | null {
  if (typeof localStorage === 'undefined') return null
  const raw = localStorage.getItem(key)
  if (raw === null) return null
  const n = Number(raw)
  return Number.isFinite(n) ? Math.max(0, Math.floor(n)) : null
}

let flushTimer: ReturnType<typeof setTimeout> | null = null
let anonAttempted = false
// Identity changes await the actual in-flight request, not an empty early return.
let pendingFlush: Promise<Record<string, EarnResult['status']>> | null = null

export function useStardust() {
  const earned = useState('stardust-earned', () => 0)
  const spent = useState('stardust-spent', () => 0)
  const loaded = useState('stardust-loaded', () => false)
  const identity = useAuthUser()
  const confirmed = useState<number | null>('wallet-confirmed', () => null)
  const confirmedUid = useState<string | null>('wallet-confirmed-uid', () => null)
  const imported = useState('wallet-imported', () => false)
  const outbox = useState<OutboxItem[]>('wallet-outbox', () => [])
  const walletLoaded = useState('wallet-loaded', () => false)
  const flushing = useState('wallet-flushing', () => false)

  const localMode = () =>
    typeof localStorage !== 'undefined' && localStorage.getItem(LOCAL_MODE_KEY) === '1'

  const legacyBalance = computed(() => Math.max(0, earned.value - spent.value))
  const pending = computed(() => outbox.value.reduce((sum, item) =>
    // While no server balance has ever been fetched the display falls
    // back to the legacy counters — which an unflushed import would then
    // double-count, so imports only show once confirmed exists.
    sum + (item.uid && item.uid !== identity.value?.uid
      || item.body.kind === 'legacy-import' && confirmed.value === null ? 0 : item.estimate), 0))

  const balance = computed(() => {
    if (localMode()) return legacyBalance.value
    const ownBalance = confirmedUid.value && confirmedUid.value !== identity.value?.uid ? null : confirmed.value
    return Math.max(0, (ownBalance ?? legacyBalance.value) + pending.value)
  })

  /** Load-once guard for the pre-ledger counters (import source). The
   * wallet cache and outbox load alongside so the title screen's balance
   * is warm from the same call sites that always primed this. */
  function load() {
    loadWallet()
    if (loaded.value || typeof localStorage === 'undefined') return
    loaded.value = true
    const storedEarned = readNum(EARNED_KEY)
    if (storedEarned !== null) {
      earned.value = storedEarned
      spent.value = readNum(SPENT_KEY) ?? 0
      return
    }
    // Migration from the balance-only model: whatever the player holds
    // counts as earned-and-unspent, so nobody loses a star.
    earned.value = readNum(STORAGE_KEY) ?? 0
    spent.value = 0
    if (earned.value > 0) persist()
  }

  function persist() {
    if (typeof localStorage === 'undefined') return
    localStorage.setItem(EARNED_KEY, String(earned.value))
    localStorage.setItem(SPENT_KEY, String(spent.value))
    localStorage.setItem(STORAGE_KEY, String(legacyBalance.value))
  }

  function loadWallet() {
    if (walletLoaded.value || typeof localStorage === 'undefined') return
    walletLoaded.value = true
    try {
      const cache = JSON.parse(localStorage.getItem(CACHE_KEY) ?? 'null')
      if (cache && typeof cache.balance === 'number') {
        confirmed.value = Math.max(0, Math.floor(cache.balance))
        confirmedUid.value = typeof cache.uid === 'string' ? cache.uid : null
        imported.value = Boolean(cache.imported)
      }
    } catch { /* stale cache: refresh() rebuilds it */ }
    try {
      const box = JSON.parse(localStorage.getItem(OUTBOX_KEY) ?? '[]')
      if (Array.isArray(box)) {
        outbox.value = box.filter((i): i is OutboxItem =>
          !!i && typeof i.key === 'string' && !!i.body && typeof i.estimate === 'number')
      }
    } catch { /* corrupt outbox: start empty */ }
  }

  function persistWallet() {
    if (typeof localStorage === 'undefined') return
    localStorage.setItem(CACHE_KEY, JSON.stringify({ uid: confirmedUid.value, balance: confirmed.value, imported: imported.value }))
    localStorage.setItem(OUTBOX_KEY, JSON.stringify(outbox.value))
  }

  /** Settled current user; optionally mints the lazy anonymous identity
   * (once per session — a disabled provider must not retry-spam). */
  async function settledUser(createAnon: boolean) {
    const fb = await firebaseReady()
    await fb.auth.authStateReady()
    const user = fb.auth.currentUser
    if (user || !createAnon || anonAttempted) return user
    anonAttempted = true
    if (await nativeHadUid()) {
      useNativeIdentityNudge().recover()
      return null
    }
    try {
      await rememberNativeUid('pending')
      if (fb.auth.currentUser) {
        await rememberNativeUid(fb.auth.currentUser.uid)
        return fb.auth.currentUser
      }
      const created = await fb.authMod.signInAnonymously(fb.auth).catch(async (error) => {
        // A rejected first attempt (for example offline) acquired no identity.
        // Only that explicit failure can release this visit's pending marker.
        // A crash or a later failure to persist a returned UID remains guarded.
        if (fb.auth.currentUser) await rememberNativeUid(fb.auth.currentUser.uid)
        else await releaseFailedNativeUidReservation()
        throw error
      })
      await rememberNativeUid(created.user.uid)
      return created.user
    } catch (error) {
      console.warn('Anonymous wallet identity unavailable — earnings stay queued.', error)
      return null
    }
  }

  /** Queue a play-earned grant. The server recomputes the amount; the
   * local estimate only keeps the header instant. */
  function earnEvent(event: EarnEvent) {
    load()
    const v = validateEarnEvent(event, Date.now())
    if (!v.ok || !v.key || v.amount === undefined) {
      console.warn('Dropped malformed earn event.', event, v.error)
      return
    }
    if (localMode()) {
      earned.value += v.amount
      persist()
      return
    }
    loadWallet()
    const uid = useAuthUser().value?.uid
    if (outbox.value.some(i => i.key === v.key && !i.sourceUid && (!i.uid || i.uid === uid))) return
    outbox.value = [...outbox.value, { uid, key: v.key, body: event, estimate: v.amount, at: Date.now() }]
    useNativeIdentityNudge().firstEarn()
    persistWallet()
    scheduleFlush()
  }

  function scheduleFlush(delay = 2500) {
    if (flushTimer) clearTimeout(flushTimer)
    flushTimer = setTimeout(() => { void flush() }, delay)
  }

  /** Drain the outbox into the ledger. Returns per-key statuses so the
   * import flow can see whether its event actually landed. */
  function flush(): Promise<Record<string, EarnResult['status']>> {
    if (!pendingFlush) pendingFlush = drainOutbox().finally(() => { pendingFlush = null })
    return pendingFlush
  }

  async function drainOutbox(): Promise<Record<string, EarnResult['status']>> {
    const seen: Record<string, EarnResult['status']> = {}
    if (localMode()) return seen
    loadWallet()
    if (outbox.value.length === 0) return seen
    const user = await settledUser(true)
    if (!user) return seen
    const fb = await firebaseReady()
    // Bind new guest events before any request. A failed sign-out flush must
    // never let a different account replay this account's queued earnings.
    outbox.value = outbox.value.map(item => item.uid ? item : { ...item, uid: user.uid })
    persistWallet()
    flushing.value = true
    try {
      const token = await user.getIdToken()
      while (outbox.value.length > 0) {
        if (fb.auth.currentUser?.uid !== user.uid) break
        const first = outbox.value.find(item => item.uid === user.uid)
        if (!first) break
        // Transferred events retain their original ledger owner so the server
        // can recognize a credit whose HTTP response was lost before sign-in.
        const sourceUid = first.sourceUid
        const chunk = outbox.value.filter(item => item.uid === user.uid && item.sourceUid === sourceUid).slice(0, EARN_BATCH_MAX)
        const res = await apiFetch<{ results: EarnResult[]; balance: number }>('/api/wallet/earn', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, ...(await appCheckHeader()) },
          body: { events: chunk.map(i => i.body), epoch: PROGRESS_EPOCH, ...(sourceUid ? { sourceUid } : {}) },
        })
        // 🗃️ v6.36: match verdicts to events by index (or an older
        // server's invalid:<n>, or the key) rather than assuming the key
        // came back — a rejected event used to be unnameable and stuck.
        const { doneKeys: done, seen: batch } = applyEarnResults(chunk, res.results)
        Object.assign(seen, batch)
        const chunkKeys = new Set(chunk.map(i => i.key))
        outbox.value = outbox.value.filter(i => i.uid !== user.uid || i.sourceUid !== sourceUid || !done.has(i.key))
        if (fb.auth.currentUser?.uid === user.uid) {
          confirmed.value = Math.max(0, Math.floor(res.balance))
          confirmedUid.value = user.uid
        }
        persistWallet()
        // Anything in this chunk that came back deferred stays queued —
        // stop rather than spin on it.
        if (outbox.value.some(i => i.uid === user.uid && i.sourceUid === sourceUid && chunkKeys.has(i.key))) break
      }
    } catch (error) {
      console.warn('Wallet flush unavailable — earnings stay queued.', error)
    } finally {
      flushing.value = false
    }
    return seen
  }

  /** Pull the confirmed balance (and, since v4.93, the account's
   * server-side collection riding the same response). */
  async function refresh() {
    if (localMode()) return
    loadWallet()
    const user = await settledUser(false)
    if (!user) return
    try {
      const token = await user.getIdToken()
      const res = await apiFetch<{
        balance: number; imported: boolean
        creatures: string[] | null; creaturesImported: boolean
      }>('/api/wallet', {
        headers: { Authorization: `Bearer ${token}`, ...(await appCheckHeader()) },
      })
      if ((await firebaseReady()).auth.currentUser?.uid !== user.uid) return
      confirmed.value = Math.max(0, Math.floor(res.balance))
      confirmedUid.value = user.uid
      imported.value = res.imported
      useOwnedCreatures().noteServer(res.creatures, res.creaturesImported)
      persistWallet()
    } catch (error) {
      console.warn('Wallet read unavailable — showing the cached balance.', error)
    }
  }

  /** One-time amnesty: move the pre-ledger counters into the account's
   * ledger, then zero them locally — the ledger owns the value now, and
   * a later Google sign-in on this device must not re-import them. */
  async function ensureImported() {
    if (localMode() || imported.value) return
    load()
    if (confirmed.value === null) return // need the server's imported flag first
    if (earned.value <= 0) return // nothing to move; a richer device can still import later
    earnEvent({ kind: 'legacy-import', earned: earned.value, spent: spent.value })
    const statuses = await flush()
    const landed = statuses['legacy-import']
    if (landed === 'credited' || landed === 'duplicate') {
      imported.value = true
      earned.value = 0
      spent.value = 0
      persist()
      persistWallet()
    }
  }

  /** Cloud sync calls this after every pull-and-merge: by then the
   * legacy counters (and the merged owned list) hold the account's full
   * pre-ledger picture. */
  async function onCloudMerged() {
    if (localMode()) return
    await refresh()
    await ensureImported()
    await useOwnedCreatures().ensureImportedOnServer()
    await flush()
  }

  /** 🧹 v5.04 (widened v5.05): wipe the caller's OWN vault, collection
   * and progress — server docs first (wallet+tx, ownership, users meta
   * with its saves), then EVERY local progress key via the same list
   * the global epoch wipe uses, so neither side can quietly restore
   * the other. The caller reloads immediately after — the sweep must
   * not leave a window where a heartbeat re-persists stale state.
   * Reached via the ?reset-progress=1 flow only. */
  async function resetProgress(): Promise<boolean> {
    const user = await settledUser(false)
    if (!user) return false
    try {
      const token = await user.getIdToken()
      await apiFetch('/api/account/reset', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, ...(await appCheckHeader()) },
        body: { confirm: 'wipe', epoch: PROGRESS_EPOCH },
      })
    } catch (error) {
      console.warn('Progress reset unavailable.', error)
      return false
    }
    // Stop late heartbeat mirrors before the synchronous local sweep/reload.
    await clearNativePersistence()
    if (typeof localStorage !== 'undefined') {
      for (const key of PROGRESS_KEYS) {
        try { localStorage.removeItem(key) } catch { /* private mode */ }
      }
      // Parked saves use a per-UID suffix; the classification list contains
      // their prefix, so sweep those slots too before native mirroring resumes.
      try {
        for (let i = localStorage.length - 1; i >= 0; i--) {
          const key = localStorage.key(i)
          if (key?.startsWith('elemental-dispatch-run-parked:')) localStorage.removeItem(key)
        }
      } catch { /* private mode */ }
    }
    confirmed.value = 0
    confirmedUid.value = user.uid
    imported.value = false
    outbox.value = []
    earned.value = 0
    spent.value = 0
    useOwnedCreatures().reset()
    useStarweave().clear()
    return true
  }

  /** Sign-out: the balance belongs to the account, not the device. */
  function onSignedOut() {
    confirmed.value = null
    confirmedUid.value = null
    imported.value = false
    anonAttempted = false
    persistWallet()
  }

  /** A successful server wallet transfer is the only cross-UID rebinding. */
  function transferOutbox(from: string, to: string) {
    loadWallet()
    if (useAuthUser().value?.uid !== to) return
    outbox.value = outbox.value.map(item => item.uid === from ? { ...item, uid: to, sourceUid: item.sourceUid ?? from } : item)
    persistWallet()
  }

  /** Only an authenticated response for the current UID may replace this mirror. */
  function noteServerBalance(value: number, uid: string): boolean {
    if (useAuthUser().value?.uid !== uid || !Number.isSafeInteger(value) || value < 0) return false
    confirmed.value = value
    confirmedUid.value = uid
    persistWallet()
    return true
  }

  /** Explicit local-wallet test mode only; this is never submitted to the server. */
  function adjustLocalBalance(delta: number): boolean {
    load()
    if (!localMode() || !Number.isSafeInteger(delta)
      || !Number.isSafeInteger(legacyBalance.value + delta) || legacyBalance.value + delta < 0) return false
    if (delta < 0) spent.value -= delta
    else earned.value += delta
    persist()
    return true
  }

  /** Fail-closed purchase. */
  async function spendSku(sku: SpendSku): Promise<SpendOutcome> {
    load()
    if (localMode()) {
      const price = spendPrice(sku) ?? 0
      if (price <= 0 || legacyBalance.value < price) return { ok: false, reason: 'funds' }
      spent.value += price
      persist()
      return { ok: true }
    }
    loadWallet()
    const user = await settledUser(true)
    if (!user) return { ok: false, reason: 'signin' }
    if (outbox.value.length > 0) await flush()
    try {
      const token = await user.getIdToken()
      const res = await apiFetch<{ ok: boolean; reason?: string; balance: number }>('/api/wallet/spend', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, ...(await appCheckHeader()) },
        body: { sku, epoch: PROGRESS_EPOCH },
      })
      if ((await firebaseReady()).auth.currentUser?.uid !== user.uid) return { ok: false, reason: 'signin' }
      confirmed.value = Math.max(0, Math.floor(res.balance))
      confirmedUid.value = user.uid
      persistWallet()
      if (res.ok) return { ok: true }
      // Short on the server but covered on screen? The gap is stardust
      // still syncing — tell the player to give it a moment.
      return { ok: false, reason: pending.value > 0 ? 'syncing' : 'funds' }
    } catch (error) {
      console.warn('Wallet spend unavailable.', error)
      return { ok: false, reason: 'offline' }
    }
  }

  /**
   * 🎴 Open a booster pack (v4.93, closes audit E7): the SERVER rolls
   * with crypto entropy, judges dupes against the account collection,
   * settles debit-minus-refunds atomically and grants the new creatures
   * — the client only celebrates the cards it's handed. 'buy' debits
   * 700; 'streak' is the 30-day free pack (spacing-capped server-side).
   * Local mode keeps the pre-v4.93 device-local roll for the harness.
   */
  async function openBooster(mode: 'buy' | 'streak'): Promise<
    | { ok: true; cards: { defId: string; dupe: boolean; refund: number }[] }
    | { ok: false; reason: 'signin' | 'offline' | 'funds' | 'syncing' }
  > {
    load()
    const ownedStore = useOwnedCreatures()
    if (localMode()) {
      if (mode === 'buy') {
        if (legacyBalance.value < BOOSTER.price) return { ok: false, reason: 'funds' }
        spent.value += BOOSTER.price
      }
      const cards = rollBoosterPack().map((defId) => {
        const dupe = ownedStore.owned.value.includes(defId)
        if (!dupe) ownedStore.own(defId)
        return { defId, dupe, refund: dupe ? boosterDupeRefund(getCreatureDef(defId).rarity) : 0 }
      })
      earned.value += cards.reduce((sum, c) => sum + c.refund, 0)
      persist()
      return { ok: true, cards }
    }
    loadWallet()
    const user = await settledUser(true)
    if (!user) return { ok: false, reason: 'signin' }
    if (outbox.value.length > 0) await flush()
    try {
      const token = await user.getIdToken()
      const res = await apiFetch<
        | { ok: true; cards: { defId: string; dupe: boolean; refund: number }[]; balance: number }
        | { ok: false; reason: string; balance: number }
      >('/api/booster/open', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, ...(await appCheckHeader()) },
        body: { mode, epoch: PROGRESS_EPOCH },
      })
      if ((await firebaseReady()).auth.currentUser?.uid !== user.uid) return { ok: false, reason: 'signin' }
      confirmed.value = Math.max(0, Math.floor(res.balance))
      confirmedUid.value = user.uid
      persistWallet()
      if (!res.ok) {
        // 'early' (streak spacing) surfaces as the try-again notice.
        return { ok: false, reason: res.reason === 'funds' ? 'funds' : 'syncing' }
      }
      for (const card of res.cards) {
        if (!card.dupe) ownedStore.own(card.defId)
      }
      return { ok: true, cards: res.cards }
    } catch (error) {
      console.warn('Booster open unavailable.', error)
      return { ok: false, reason: 'offline' }
    }
  }

  /**
   * 💳 Real-money stardust (v4.94). Starting a checkout redirects the
   * whole page to Stripe; the return flow lands back on the title with
   * the session id, and confirmPurchase settles it instantly (the
   * webhook is the backstop for a closed tab — both paths idempotent).
   */
  async function startStardustCheckout(packId: string): Promise<boolean> {
    if (localMode()) return false
    const user = await settledUser(true)
    if (!user) return false
    try {
      const token = await user.getIdToken()
      const res = await apiFetch<{ url: string | null }>('/api/stripe/checkout', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, ...(await appCheckHeader()) },
        body: { packId, epoch: PROGRESS_EPOCH },
      })
      if (!res.url) return false
      window.location.href = res.url
      return true
    } catch (error) {
      console.warn('Checkout unavailable.', error)
      return false
    }
  }

  /** Returns the stardust credited (0 when already settled or not paid). */
  async function confirmPurchase(sessionId: string): Promise<number> {
    if (localMode()) return 0
    const user = await settledUser(false)
    if (!user) return 0
    try {
      const token = await user.getIdToken()
      const res = await apiFetch<{ credited: boolean; amount: number; balance?: number }>('/api/stripe/confirm', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, ...(await appCheckHeader()) },
        body: { sessionId },
      })
      if ((await firebaseReady()).auth.currentUser?.uid !== user.uid) return 0
      if (typeof res.balance === 'number') {
        confirmed.value = Math.max(0, Math.floor(res.balance))
        confirmedUid.value = user.uid
        persistWallet()
      }
      return res.credited ? res.amount : 0
    } catch (error) {
      console.warn('Purchase confirm unavailable — the webhook will settle it.', error)
      return 0
    }
  }

  /**
   * ☁️ Merge the cloud's LEGACY counters (pre-ledger devices still push
   * them) so the one-time import sees the account's whole picture.
   */
  function adopt(remote: { earned?: number; spent?: number; balance?: number }) {
    load()
    const next = mergeStardust({ earned: earned.value, spent: spent.value }, remote)
    if (next.earned !== earned.value || next.spent !== spent.value) {
      earned.value = next.earned
      spent.value = next.spent
      persist()
    }
  }

  return {
    balance, pending, earned, spent, load, adopt,
    earnEvent, spendSku, openBooster, flush, refresh, onCloudMerged, onSignedOut,
    startStardustCheckout, confirmPurchase, resetProgress,
    noteServerBalance, adjustLocalBalance, transferOutbox, isLocalMode: localMode, getWalletUser: settledUser,
  }
}
